import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import EChartsRenderer from '../../components/EChartsRenderer';
import type { PoolingBand, TimingAdjusted, TimingHeatmap, TimingStateRow } from '../../types/contactOptimization';

// Same four bands as Pulse/Timing's old hand-rolled cells (DESIGN.md
// section 5) -- kept identical here so the heatmap's colors don't shift
// just because the rendering engine changed. Adjusted mode (below) is
// deliberately made to reuse this exact same 4-color palette rather than
// a continuous gradient -- one color language for the whole page, not
// two, and no muddy blended colors in the middle of a scale.
export const BAND_COLORS: Record<PoolingBand, string> = {
  prime: '#185fa5',
  good: '#85b7eb',
  fill: '#dcdcd6',
  avoid: '#c0443f',
};
export const BAND_TEXT_COLORS: Record<PoolingBand, string> = {
  prime: '#f5f5f0',
  good: '#1a1a18',
  fill: '#1a1a18',
  avoid: '#f5f5f0',
};
export const BAND_LABELS: Record<PoolingBand, string> = {
  prime: 'Prime',
  good: 'Good',
  fill: 'Fill',
  avoid: 'Avoid',
};
const BAND_ORDER: PoolingBand[] = ['avoid', 'fill', 'good', 'prime'];

export function pct(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '\u2013';
  return `${(value * 100).toFixed(digits)}%`;
}

export function ratioX(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '\u2013';
  return `${value.toFixed(digits)}x`;
}

export function fmtUsd(value: number | null | undefined): string {
  if (value === null || value === undefined) return '\u2013';
  return `$${Math.round(value).toLocaleString()}`;
}

// Which of the heatmap's two independent metrics drives each cell's color
// and number. "Both" is the default -- see TimingHeatmapChart's own
// comment for why it isn't a simple blend of the two.
export type TimingHeatmapMetric = 'connect' | 'value' | 'both';

const ROW_HEIGHT = 24;
const HEADER_HEIGHT = 30;
const LABEL_COL_WIDTH = 64;

interface CellDatum {
  value: [number, number, number];
  raw: number | null;
  tooltip: string;
  labelText: string;
  itemStyle: { color: string };
  label: { color: string };
}

/**
 * A compact legend + one-line explanation of what the colors mean.
 * Deliberately rendered as plain React/MUI outside the chart canvas, not
 * as an ECharts `visualMap` -- visualMap's own layout (orient/itemWidth/
 * itemHeight) kept colliding with the chart's own axis labels and, in
 * continuous mode, drawing a stray drag-handle bar over the grid. A
 * hand-built legend row is a few lines of Flexbox and can't misbehave
 * that way.
 *
 * There used to be a second version of this caption for a whole separate
 * "adjusted" heatmap mode, and a pointer to a per-cell "model check"
 * tooltip line. Both are gone -- see TimingHeatmapChart's own comment
 * for why -- so this always describes the one, real-results grid.
 */
export function BandLegend({ metric = 'connect' }: { metric?: TimingHeatmapMetric }) {
  const basis =
    metric === 'value'
      ? 'how likely a lead is to turn into paid business, and how big that business tends to be'
      : metric === 'both'
        ? "whichever of connect rate or dollar value looks best for that hour — hover a cell to see which one's driving the color"
        : 'real call results';
  return (
    <Stack direction="row" spacing={2} alignItems="center" rowGap={0.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
      {BAND_ORDER.map((band) => (
        <Stack key={band} direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 14, height: 10, borderRadius: '2px', bgcolor: BAND_COLORS[band], flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">{BAND_LABELS[band]}</Typography>
        </Stack>
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {`\u2014 based on ${basis}, only shown when we have enough data to be confident`}
      </Typography>
    </Stack>
  );
}

/**
 * State x hour heatmap, replacing the old hand-rolled CSS-grid-of-<Box>
 * version with a real ECharts heatmap: per-cell number labels always
 * visible (not hover-only), and PNG export for free from
 * EChartsRenderer. Colors are set explicitly per cell (see BandLegend's
 * comment for why this isn't a `visualMap`).
 *
 * This used to have a second, whole-grid "adjusted" mode you toggled to,
 * plus an extra "model check" line in each cell's tooltip and a
 * standalone "where the hour matters most" panel comparing the two.
 * None of that was ever part of the original recommendation prototype
 * this page is rebuilding (CC-PROTOTYPE-AUDIT.md) -- it was an add-on
 * once a prediction model existed, and made the grid harder to read for
 * not much payoff. Removed; this grid now shows exactly one thing, the
 * real results. The model's own hour-vs-lead-mix comparison still lives
 * in `CompareStatesChart` (a real vs. model-implied line pair for
 * whichever states you pick), scoped to states you're actually deciding
 * between instead of shown on every cell.
 */
export function TimingHeatmapChart({
  heatmap,
  metric = 'both',
}: {
  heatmap: TimingHeatmap;
  metric?: TimingHeatmapMetric;
}) {
  const states = heatmap.grid.map((r) => r.state);
  const hours = heatmap.hours.map((h) => h.hour_label);
  const hourIndexByHour = new Map(heatmap.hours.map((h, i) => [h.hour, i]));

  const cells: CellDatum[] = [];

  heatmap.grid.forEach((row: TimingStateRow, yIdx) => {
    row.cells.forEach((cell) => {
      const xIdx = hourIndexByHour.get(cell.hour);
      if (xIdx === undefined) return;

      // "Both" picks whichever of the two independent metrics ranks this
      // cell higher (Prime beats Good beats Fill beats Avoid) -- same
      // "qualifies on either metric" logic the reference prototype used
      // for its own recommended-hours rollup (CC-PROTOTYPE-AUDIT.md), just
      // applied per-state (matching how every other band on this page
      // already works) instead of against one floor-wide number. A
      // so-so connect rate with a strong dollar payoff still shows up as
      // a real opportunity, not hidden behind a mediocre connect-rate color.
      const connectBand = cell.band;
      const valueBand = cell.value_cost_band;
      let activeBand: PoolingBand | null;
      let drivingMetric: 'connect' | 'value' | null;
      if (metric === 'connect') {
        activeBand = connectBand;
        drivingMetric = 'connect';
      } else if (metric === 'value') {
        activeBand = valueBand;
        drivingMetric = 'value';
      } else if (connectBand && valueBand) {
        const useValue = BAND_ORDER.indexOf(valueBand) > BAND_ORDER.indexOf(connectBand);
        activeBand = useValue ? valueBand : connectBand;
        drivingMetric = useValue ? 'value' : 'connect';
      } else {
        activeBand = connectBand ?? valueBand;
        drivingMetric = connectBand ? 'connect' : valueBand ? 'value' : null;
      }
      if (!activeBand || drivingMetric == null) return;

      const ciText =
        cell.ci_low != null && cell.ci_high != null ? ` \u00b1 [${pct(cell.ci_low)}\u2013${pct(cell.ci_high)}]` : '';
      const shrinkNote =
        cell.prior_weight_calls != null && cell.connect_rate != null
          ? ` (actual rate ${pct(cell.connect_rate)}, blended toward the state average since this cell has less data)`
          : '';
      // What "Good" vs. "Avoid" actually means -- this cell's rate compared
      // against its own state's median (raw+50 method) or prior mean
      // (shrunk method). Showing the number itself, not just the color,
      // is what makes "why is this Avoid" self-explanatory in the tooltip.
      const connectMedianText =
        cell.group_median_rate != null ? `, vs. this state's median of ${pct(cell.group_median_rate)}` : '';
      const valueMedianText =
        cell.value_cost_group_median_ratio != null
          ? `, vs. this state's median of ${ratioX(cell.value_cost_group_median_ratio)}`
          : '';

      const connectLine = connectBand
        ? `Connect rate: ${BAND_LABELS[connectBand]} \u2014 ${pct(cell.shrunk_rate)}${ciText}${connectMedianText}<br/>${cell.total_connects}/${cell.total_calls} calls${shrinkNote}`
        : 'Connect rate: not enough data yet';
      const valueLine = valueBand
        ? `Value per dollar spent: ${BAND_LABELS[valueBand]} \u2014 ${ratioX(cell.value_cost_ratio)}${valueMedianText}<br/>${pct(cell.deal_rate, 1)} of ${cell.leads_dialed_value_cost.toLocaleString()} leads dialed here went on to submit an application, typically worth ${fmtUsd(cell.typical_deal_size)} in premium`
        : 'Value per dollar spent: not enough completed deals yet to estimate';
      const whyThisColor =
        metric === 'both'
          ? `<br/><br/><em>Color driven by:</em> ${drivingMetric === 'value' ? 'value per dollar spent (the stronger of the two here)' : 'connect rate (the stronger of the two here)'}.`
          : '';

      // Same precision as the tooltip (2dp for a rate, 1dp for a ratio) --
      // a rounded-to-whole-percent box label used to disagree with the
      // tooltip's more precise number underneath it for the exact same
      // cell, which read as a bug even though it was just two different
      // roundings of one real value.
      let labelText: string;
      if (metric === 'connect') labelText = pct(cell.shrunk_rate, 2);
      else if (metric === 'value') labelText = ratioX(cell.value_cost_ratio);
      else labelText = `${pct(cell.shrunk_rate, 2)} \u00b7 ${ratioX(cell.value_cost_ratio)}`;

      cells.push({
        value: [xIdx, yIdx, BAND_ORDER.indexOf(activeBand)],
        raw: cell.shrunk_rate,
        labelText,
        tooltip: `<strong>${row.state} at ${cell.hour_label}</strong><br/>${connectLine}<br/><br/>${valueLine}${whyThisColor}`,
        itemStyle: { color: BAND_COLORS[activeBand] },
        label: { color: BAND_TEXT_COLORS[activeBand] },
      });
    });
  });

  const option = React.useMemo(
    () => ({
      grid: { left: LABEL_COL_WIDTH, right: 16, top: HEADER_HEIGHT, bottom: 8, containLabel: false },
      xAxis: {
        type: 'category',
        data: hours,
        position: 'top',
        splitArea: { show: false },
        axisLabel: { fontSize: 10 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'category',
        data: states,
        inverse: true,
        axisLabel: { fontSize: 11 },
        axisTick: { show: false },
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => params.data?.tooltip ?? '',
        // ECharts' default tooltip placement flips to whichever side of
        // the cursor has more room -- which, for cells in the leftmost
        // (7am) column, meant it flipped left and rendered on top of the
        // state-name labels in the axis column. Pin it to always sit to
        // the right of the cursor (clamped so it never overflows the
        // chart's own right edge, and never left of the label column),
        // so the row labels stay readable no matter which cell you hover.
        position: (point: number[], _params: unknown, _dom: unknown, _rect: unknown, size: { contentSize: number[]; viewSize: number[] }) => {
          const [x, y] = point;
          const margin = 12;
          let left = x + margin;
          const maxLeft = size.viewSize[0] - size.contentSize[0] - margin;
          if (left > maxLeft) left = Math.max(maxLeft, LABEL_COL_WIDTH + margin);
          let top = y - size.contentSize[1] / 2;
          top = Math.min(Math.max(top, margin), size.viewSize[1] - size.contentSize[1] - margin);
          return [left, top];
        },
      },
      // ECharts' heatmap series hard-requires a visualMap to be registered
      // in the option or it throws ("Heatmap must use with visualMap"),
      // even though we set each cell's real color explicitly via its own
      // `itemStyle` below (which takes priority over whatever this would
      // compute). Kept hidden and inert so it can't reproduce the
      // layout/legend bugs a visible one caused earlier.
      visualMap: { show: false, min: 0, max: BAND_ORDER.length - 1, dimension: 2, seriesIndex: 0 },
      series: [
        {
          type: 'heatmap',
          data: cells,
          label: {
            show: true,
            fontSize: 9,
            fontWeight: 600 as const,
            formatter: (params: any) => params.data?.labelText ?? '',
          },
          itemStyle: { borderColor: 'rgba(255,255,255,0.5)', borderWidth: 1.5 },
          emphasis: { itemStyle: { borderColor: 'rgba(0,0,0,0.45)', borderWidth: 2 } },
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [heatmap, metric],
  );

  const height = Math.max(280, states.length * ROW_HEIGHT + HEADER_HEIGHT + 16);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <EChartsRenderer option={option} height={height} />
    </Box>
  );
}

/**
 * "Connect rate by hour, all states combined" -- the reference
 * prototype's own Step 2b baseline chart (CC_Output.html), never built
 * here before (CC-PROTOTYPE-AUDIT.md's smaller-gap item #4). Derived
 * entirely client-side from the heatmap grid already on the page -- no
 * new warehouse round trip -- by summing calls/connects per hour across
 * whichever states are currently shown (top-N or all).
 *
 * Also shades a min-to-max band behind the average line, built from each
 * state's own pooled (shrunk) rate at that hour -- an operations leader's
 * first question on seeing a floor-wide average is usually "does that
 * actually apply everywhere, or is it one big state hiding a lot of
 * disagreement" -- the band answers that without needing to cross-check
 * the heatmap row by row.
 */
export function PooledByHourChart({
  heatmap,
  metric = 'connect',
}: {
  heatmap: TimingHeatmap;
  metric?: TimingHeatmapMetric;
}) {
  const byHour = React.useMemo(() => {
    const totals = new Map<
      number,
      {
        calls: number;
        connects: number;
        connectRates: number[];
        valueWeight: number;
        valueWeightedRatio: number;
        valueRatios: number[];
      }
    >();
    for (const h of heatmap.hours) {
      totals.set(h.hour, { calls: 0, connects: 0, connectRates: [], valueWeight: 0, valueWeightedRatio: 0, valueRatios: [] });
    }
    for (const row of heatmap.grid) {
      for (const cell of row.cells) {
        const t = totals.get(cell.hour);
        if (!t) continue;
        t.calls += cell.total_calls;
        t.connects += cell.total_connects;
        if (cell.shrunk_rate != null) t.connectRates.push(cell.shrunk_rate);
        // Same "leads dialed for this metric" weight the backend uses as
        // this cell's own sample size (not total_calls -- deal rate is
        // measured on distinct leads, not raw call attempts) -- see
        // queries.timing_value_cost_cells_sql's docstring.
        if (cell.value_cost_ratio != null && cell.leads_dialed_value_cost) {
          t.valueWeight += cell.leads_dialed_value_cost;
          t.valueWeightedRatio += cell.value_cost_ratio * cell.leads_dialed_value_cost;
          t.valueRatios.push(cell.value_cost_ratio);
        }
      }
    }
    return heatmap.hours.map((h) => {
      const t = totals.get(h.hour)!;
      return {
        hour_label: h.hour_label,
        connectRate: t.calls > 0 ? t.connects / t.calls : null,
        calls: t.calls,
        connects: t.connects,
        connectMin: t.connectRates.length ? Math.min(...t.connectRates) : null,
        connectMax: t.connectRates.length ? Math.max(...t.connectRates) : null,
        stateCount: t.connectRates.length,
        // A leads-dialed-weighted average of each state's own value/cost
        // ratio at this hour -- not re-derived from scratch (that would
        // need raw deal counts/premium the frontend doesn't have), just a
        // weighted blend of numbers the backend already computed per cell.
        valueRatio: t.valueWeight > 0 ? t.valueWeightedRatio / t.valueWeight : null,
        valueMin: t.valueRatios.length ? Math.min(...t.valueRatios) : null,
        valueMax: t.valueRatios.length ? Math.max(...t.valueRatios) : null,
        valueStateCount: t.valueRatios.length,
      };
    });
  }, [heatmap]);

  const connectRates = byHour.map((r) => r.connectRate).filter((r): r is number => r != null);
  const connectMedian = connectRates.length ? [...connectRates].sort((a, b) => a - b)[Math.floor(connectRates.length / 2)] : null;
  const valueRatios = byHour.map((r) => r.valueRatio).filter((r): r is number => r != null);
  const valueMedian = valueRatios.length ? [...valueRatios].sort((a, b) => a - b)[Math.floor(valueRatios.length / 2)] : null;

  const option = React.useMemo(() => {
    const hours = byHour.map((r) => r.hour_label);

    const connectBandSeries = [
      {
        name: 'Floor (invisible base)',
        type: 'line',
        stack: 'connectRange',
        symbol: 'none',
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        data: byHour.map((r) => (r.connectMin != null ? +(r.connectMin * 100).toFixed(3) : 0)),
        silent: true,
        tooltip: { show: false },
        // Stacking helper only -- never shown in the legend.
        legendHoverLink: false,
        yAxisIndex: 0,
      },
      {
        name: 'Range across states',
        type: 'line',
        stack: 'connectRange',
        symbol: 'none',
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0.12, color: '#185fa5' },
        itemStyle: { color: 'rgba(24, 95, 165, 0.25)' },
        data: byHour.map((r) =>
          r.connectMin != null && r.connectMax != null ? +((r.connectMax - r.connectMin) * 100).toFixed(3) : 0,
        ),
        silent: true,
        tooltip: { show: false },
        yAxisIndex: 0,
      },
    ];
    const connectLine = {
      name: 'Connect rate',
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 7,
      lineStyle: { width: 2.5, color: '#185fa5' },
      itemStyle: { color: '#185fa5' },
      data: byHour.map((r) => (r.connectRate != null ? +(r.connectRate * 100).toFixed(3) : null)),
      // In "Both" mode this line's points sit close to the Value/Cost
      // line's -- both labels defaulting to "top" put two short text
      // strings right on top of each other wherever the lines run close
      // together (colliding, unreadable). Pinning this one above its
      // points and the other (below) fixes that without hiding either
      // number -- single-metric mode has only one line, so no collision
      // risk there and it stays "top" either way.
      label: {
        show: true,
        position: 'top',
        fontSize: 10,
        formatter: (params: any) => (params.value != null ? `${params.value.toFixed(1)}%` : ''),
      },
      markLine:
        metric !== 'both' && connectMedian != null
          ? {
              silent: true,
              symbol: 'none',
              lineStyle: { type: 'dashed', color: '#c0443f' },
              label: { formatter: () => `Median ${pct(connectMedian)}`, fontSize: 10, color: '#c0443f' },
              data: [{ yAxis: +(connectMedian * 100).toFixed(3) }],
            }
          : undefined,
      yAxisIndex: 0,
    };

    const valueBandSeries = [
      {
        name: 'Floor (invisible base, value)',
        type: 'line',
        stack: 'valueRange',
        symbol: 'none',
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        data: byHour.map((r) => (r.valueMin != null ? +r.valueMin.toFixed(3) : 0)),
        silent: true,
        tooltip: { show: false },
        // Stacking helper only -- never shown in the legend.
        legendHoverLink: false,
        yAxisIndex: metric === 'both' ? 1 : 0,
      },
      {
        name: 'Range across states',
        type: 'line',
        stack: 'valueRange',
        symbol: 'none',
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0.12, color: '#b8860b' },
        itemStyle: { color: 'rgba(184, 134, 11, 0.25)' },
        data: byHour.map((r) => (r.valueMin != null && r.valueMax != null ? +(r.valueMax - r.valueMin).toFixed(3) : 0)),
        silent: true,
        tooltip: { show: false },
        yAxisIndex: metric === 'both' ? 1 : 0,
      },
    ];
    const valueLine = {
      name: 'Value/Cost Ratio',
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 7,
      lineStyle: { width: 2.5, color: '#b8860b' },
      itemStyle: { color: '#b8860b' },
      data: byHour.map((r) => (r.valueRatio != null ? +r.valueRatio.toFixed(3) : null)),
      // See connectLine's label comment above -- pinned below its own
      // points only in "Both" mode so the two lines' labels can't land on
      // top of each other; single-metric mode keeps "top" since there's
      // only one line on screen.
      label: {
        show: true,
        position: metric === 'both' ? 'bottom' : 'top',
        fontSize: 10,
        formatter: (params: any) => (params.value != null ? ratioX(params.value) : ''),
      },
      markLine:
        metric !== 'both' && valueMedian != null
          ? {
              silent: true,
              symbol: 'none',
              lineStyle: { type: 'dashed', color: '#8a6300' },
              label: { formatter: () => `Median ${ratioX(valueMedian)}`, fontSize: 10, color: '#8a6300' },
              data: [{ yAxis: +valueMedian.toFixed(3) }],
            }
          : undefined,
      yAxisIndex: metric === 'both' ? 1 : 0,
    };

    const tooltipFormatter = (params: any[]) => {
      const idx = params[0]?.dataIndex ?? 0;
      const row = byHour[idx];
      const marker = (seriesName: string) => params.find((p) => p.seriesName === seriesName)?.marker ?? '';
      const lines = [`<strong>${row.hour_label}</strong>`];
      if (metric !== 'value') {
        const spread =
          row.connectMin != null && row.connectMax != null
            ? ` (range across ${row.stateCount} states: ${pct(row.connectMin)}\u2013${pct(row.connectMax)})`
            : '';
        lines.push(`${marker('Connect rate')}Connect rate: ${pct(row.connectRate)} \u2014 ${row.connects.toLocaleString()} / ${row.calls.toLocaleString()} calls${spread}`);
      }
      if (metric !== 'connect') {
        const spread =
          row.valueMin != null && row.valueMax != null
            ? ` (range across ${row.valueStateCount} states: ${ratioX(row.valueMin)}\u2013${ratioX(row.valueMax)})`
            : '';
        lines.push(`${marker('Value/Cost Ratio')}Value/Cost Ratio: ${ratioX(row.valueRatio)}${spread}`);
      }
      return lines.join('<br/>');
    };

    if (metric === 'connect') {
      return {
        legend: {
          top: 0,
          textStyle: { fontSize: 11 },
          data: ['Connect rate', 'Range across states'],
        },
        grid: { left: 48, right: 16, top: 40, bottom: 32 },
        xAxis: { type: 'category', data: hours, axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${v.toFixed(1)}%`, fontSize: 11 } },
        tooltip: { trigger: 'axis', formatter: tooltipFormatter },
        series: [...connectBandSeries, connectLine],
      };
    }
    if (metric === 'value') {
      return {
        legend: {
          top: 0,
          textStyle: { fontSize: 11 },
          data: ['Value/Cost Ratio', 'Range across states'],
        },
        grid: { left: 56, right: 16, top: 40, bottom: 32 },
        xAxis: { type: 'category', data: hours, axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value', axisLabel: { formatter: (v: number) => ratioX(v), fontSize: 11 } },
        tooltip: { trigger: 'axis', formatter: tooltipFormatter },
        series: [...valueBandSeries, valueLine],
      };
    }
    // Both: dual axis, no shaded bands (two overlapping ranges gets visually
    // busy) -- the point of "both" here is seeing whether the two trends
    // move together across the day, not re-showing the per-state spread
    // (that's what the heatmap and its own band toggle are for).
    return {
      legend: {
        top: 0,
        textStyle: { fontSize: 11 },
        data: ['Connect rate', 'Value/Cost Ratio'],
      },
      grid: { left: 48, right: 56, top: 40, bottom: 32 },
      xAxis: { type: 'category', data: hours, axisLabel: { fontSize: 11 } },
      yAxis: [
        {
          type: 'value',
          axisLabel: { formatter: (v: number) => `${v.toFixed(1)}%`, fontSize: 11, color: '#185fa5' },
        },
        {
          type: 'value',
          axisLabel: { formatter: (v: number) => ratioX(v), fontSize: 11, color: '#8a6300' },
          splitLine: { show: false },
        },
      ],
      tooltip: { trigger: 'axis', formatter: tooltipFormatter },
      series: [connectLine, valueLine],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byHour, connectMedian, valueMedian, metric]);

  return <EChartsRenderer option={option} height={260} />;
}

/**
 * Quick "portfolio health" strip: what fraction of the state-hour cells
 * currently shown fall into each recommendation band. An operations
 * leader glancing at the page for 5 seconds should be able to tell "is
 * most of my calling time well-placed, or am I mostly dialing into Avoid
 * hours" without reading the grid row by row. Descriptive-mode only --
 * adjusted mode's bands are a per-state quartile split, so an aggregate
 * mix of them isn't a meaningful floor-wide health number the way the
 * descriptive (statistically-confident) bands are.
 */
const COMPARE_COLORS = ['#185fa5', '#c0443f', '#3f9142', '#b8862c', '#7a4fb0'];

/**
 * "Compare states" -- overlaid, translucent hour-by-hour curves for a
 * handful of hand-picked states, the same chart shape as the reference
 * prototype's own 3-state trend view. The heatmap is a great lookup
 * table but a poor comparison tool -- reading two rows 6 rows apart and
 * mentally overlaying their shapes doesn't work well. This answers "if
 * I'm choosing where to prioritize agent time between these specific
 * states, whose curve peaks when, and whose is flat" directly.
 *
 * Respects the same page-wide metric toggle as the heatmap:
 * - "connect": one solid line per state (connect rate). When a model is
 *   available, each state also gets a second, dashed line -- the
 *   model's "hour's own effect" curve for that state, on the same axis.
 *   This is where the old page-wide "What the hour causes" toggle's
 *   comparison now lives -- scoped to the handful of states you're
 *   actually deciding between, instead of a whole second heatmap.
 * - "value": one solid line per state (Value/Cost Ratio). No model
 *   overlay here -- the model only predicts pickups, not dollars, so
 *   there's no "hour-only" curve for this metric.
 * - "both": two lines per state on dual axes (solid = connect rate,
 *   dotted = Value/Cost Ratio). The model overlay is dropped in this
 *   mode -- three lines per state would be too dense to read; switch to
 *   "How likely to connect" for that comparison.
 */
export function CompareStatesChart({
  heatmap,
  adjusted,
  states,
  metric = 'connect',
}: {
  heatmap: TimingHeatmap;
  adjusted: TimingAdjusted | null;
  states: string[];
  metric?: TimingHeatmapMetric;
}) {
  const hours = heatmap.hours;
  const showModel = metric === 'connect';

  const series = React.useMemo(() => {
    const result: any[] = [];
    states.forEach((state, idx) => {
      const color = COMPARE_COLORS[idx % COMPARE_COLORS.length];
      const row = heatmap.grid.find((r) => r.state === state);
      const cellByHour = new Map((row?.cells ?? []).map((c) => [c.hour, c]));

      if (metric !== 'value') {
        const connectData = hours.map((h) => {
          const rate = cellByHour.get(h.hour)?.shrunk_rate;
          return rate != null ? +(rate * 100).toFixed(3) : null;
        });
        result.push({
          name: metric === 'both' ? `${state} \u2014 connect rate` : state,
          type: 'line' as const,
          smooth: true,
          symbol: 'none' as const,
          lineStyle: { width: 2.5, color },
          areaStyle: metric === 'connect' ? { opacity: 0.14, color } : undefined,
          data: connectData,
          connectNulls: true,
          yAxisIndex: 0,
        });

        const curve = showModel ? adjusted?.curves?.[state] : null;
        if (curve) {
          const modelData = hours.map((h) => {
            const rate = curve[String(h.hour)];
            return rate != null ? +(rate * 100).toFixed(3) : null;
          });
          result.push({
            name: `${state} (hour's own effect)`,
            type: 'line' as const,
            smooth: true,
            symbol: 'none' as const,
            lineStyle: { width: 1.5, type: 'dashed' as const, color },
            data: modelData,
            connectNulls: true,
            yAxisIndex: 0,
          });
        }
      }

      if (metric !== 'connect') {
        const valueData = hours.map((h) => {
          const ratio = cellByHour.get(h.hour)?.value_cost_ratio;
          return ratio != null ? +ratio.toFixed(3) : null;
        });
        result.push({
          name: metric === 'both' ? `${state} \u2014 value/cost` : state,
          type: 'line' as const,
          smooth: true,
          symbol: 'none' as const,
          lineStyle: { width: 2.5, color, type: metric === 'both' ? ('dotted' as const) : ('solid' as const) },
          areaStyle: metric === 'value' ? { opacity: 0.14, color } : undefined,
          data: valueData,
          connectNulls: true,
          yAxisIndex: metric === 'both' ? 1 : 0,
        });
      }
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, heatmap, adjusted, metric, showModel]);

  const option = React.useMemo(() => {
    const isRatioSeries = (name: string) => metric === 'value' || (metric === 'both' && name.includes('value/cost'));
    const tooltipFormatter = (params: any[]) =>
      `<strong>${params[0]?.axisValueLabel ?? ''}</strong><br/>${params
        .filter((p) => p.value != null)
        .map((p) => `${p.marker}${p.seriesName}: ${isRatioSeries(p.seriesName) ? ratioX(p.value) : `${p.value.toFixed(1)}%`}`)
        .join('<br/>')}`;

    if (metric === 'both') {
      return {
        grid: { left: 48, right: 56, top: 32, bottom: 32 },
        legend: { top: 0, textStyle: { fontSize: 11 }, type: 'scroll' as const },
        xAxis: { type: 'category', data: hours.map((h) => h.hour_label), axisLabel: { fontSize: 11 } },
        yAxis: [
          { type: 'value', axisLabel: { formatter: (v: number) => `${v.toFixed(1)}%`, fontSize: 11, color: '#185fa5' } },
          { type: 'value', axisLabel: { formatter: (v: number) => ratioX(v), fontSize: 11, color: '#8a6300' }, splitLine: { show: false } },
        ],
        tooltip: { trigger: 'axis', formatter: tooltipFormatter },
        series,
      };
    }
    return {
      grid: { left: 48, right: 16, top: 32, bottom: 32 },
      legend: { top: 0, textStyle: { fontSize: 11 }, type: 'scroll' as const },
      xAxis: { type: 'category', data: hours.map((h) => h.hour_label), axisLabel: { fontSize: 11 } },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => (metric === 'value' ? ratioX(v) : `${v.toFixed(1)}%`), fontSize: 11 },
      },
      tooltip: { trigger: 'axis', formatter: tooltipFormatter },
      series,
    };
  }, [hours, series, metric]);

  if (!states.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">Pick at least one state above to compare.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <EChartsRenderer option={option} height={280} />
      {showModel && adjusted && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          {'Solid line = actual results. Dashed line = the hour\u2019s own effect, once we set aside which leads happen to get called then.'}
        </Typography>
      )}
      {metric === 'both' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          {'Solid line = connect rate (left axis). Dotted line = Value/Cost Ratio (right axis). Switch to "How likely to connect" to also see the model\u2019s hour-only comparison.'}
        </Typography>
      )}
    </Box>
  );
}

